import { fonts } from 'app-v2/design-systems/typography/fonts'
import { COLOR } from 'app-v2/design-tokens'
import { Checkbox, EmptyCircle, RadioChecked } from 'app/assets/icons'
import backgroundBirthdayDesktop from 'app/assets/images/birthday/background_birthday_desktop.png'
import backgroundBirthdayMobile from 'app/assets/images/birthday/background_birthday_mobile.png'
import { ButtonLarge, ResponsiveContainer, media } from 'app/assets/styles'
import { ErrorManagement } from 'app/common/error-management'
import {
    CheckOrRadio,
    Counter,
    DatePickerField,
    Error,
    Input,
    InputHidden,
    Textarea
} from 'app/common/forms'
import { Layout } from 'app/common/layout'
import { Link } from 'app/common/link'
import { SwitchPush } from 'app/common/switch-push'
import { H1, H2, H3, SmallText, Text, TinyText } from 'app/common/typography'
import { removeLocalitiesContainerFromDOM } from 'app/common/utils'
import { Form, Formik } from 'formik'
import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Picture } from 'react-responsive-picture'
import { colors as oldColors } from 'shared/assets/styles'
import type {
    BirthdayFormData,
    BirthdayItem,
    BirthdayOpening,
    FormBirthdayBooking,
    Kid,
    RestaurantShortDTO
} from 'shared/types'
import {
    formatDate,
    formatDateTime,
    formatDisplayDateToDate,
    formatFieldDateToDateObject,
    isDateBetween,
    isTimeBetween,
    maxLength,
    regex,
    toSrcSet
} from 'shared/utils'
import { getIsFromApp, getUrlParams } from 'shared/utils/ecommerce/utils'
import styled from 'styled-components'
import { boolean, number, object, string } from 'yup'
import { RestaurantSearch } from './restaurant-search'

const { grey, yellow, red, shadowBox } = oldColors
const { flameSansRegular } = fonts
const { BIRTHDAY_COMMENTS_LIMIT } = maxLength

type Props = {
    formData: BirthdayFormData,
    dataLoaded: boolean,
    success: boolean,
    sendFormError: any,
    error: any,
    networkError: any,
    location: any,
    email: string,
    phone: string,
    kids: Array<Kid>,
    fetchProfileData: () => void,
    authenticated: ?boolean,
    fetchPageData: () => void,
    getUrlParams: () => void,
    sendForm: FormBirthdayBooking => void,
    resetError: () => void,
    reset: () => void,
    fetchOpeningHours: string => void,
    navigate: (string, any) => void,
    intl: IntlShape
}

type State = {
    openRestaurantSearchModal: boolean,
    openingHours: Array<BirthdayOpening>
}

class Birthday extends Component<Props, State> {
    state = {
        openRestaurantSearchModal: false,
        openingHours: []
    }

    async componentDidMount() {
        const {
            fetchPageData,
            location,
            authenticated,
            fetchProfileData
        } = this.props
        if (location.state) {
            await this.setOpeningHoursToState(location.state.id)
        }
        fetchPageData()
        authenticated && fetchProfileData()
    }

    componentDidUpdate() {
        this.props.success && window.scrollTo(0, 0)
    }

    componentWillUnmount() {
        this.props.reset()
    }

    formSubscription = () => {
        const { formData } = this.props
        const { minGuests, maxGuests } = formData
        const mandatory = 'form.error.field.mandatory'
        const invalidKey = 'faq.form.error.invalid'
        const invalid = {
            message: invalidKey,
            excludeEmptyString: true
        }

        return object().shape({
            restaurantName: string().required(mandatory),
            reservationDate: string()
                .required(mandatory)
                .test(
                    'isValidDate',
                    'birthday.form.restaurant.date.invalid',
                    value => {
                        if (value === undefined) {
                            return false
                        }
                        return this.isTimeAvailable(
                            formatFieldDateToDateObject(value)
                        )
                    }
                ),
            guests: number().test(
                'isValidGuests',
                invalidKey,
                value => value <= maxGuests && value >= minGuests
            ),
            giftId: string().required(mandatory),
            cakeId: string(),
            birthdate: string()
                .required(mandatory)
                .test(
                    'isValidBirthdate',
                    'birthday.form.kid.birthdate.error',
                    value => {
                        if (value === undefined) {
                            return false
                        }
                        if (value.match(regex.DATE)) {
                            const date = value.split('/')
                            const dateFormatted = `${date[2]}-${date[1]}-${date[0]}`
                            return isDateBetween(dateFormatted, 4, 13)
                        }
                        if (value.match(regex.DATE_ISO)) {
                            return isDateBetween(value, 4, 13)
                        }
                        return false
                    }
                ),
            name: string().required(mandatory),
            attendantEmail: string()
                .required(mandatory)
                .matches(regex.EMAIL, invalid),
            attendantPhone: string()
                .required(mandatory)
                .matches(regex.TEL, invalid),
            cgu: boolean()
                .required()
                .oneOf([true], mandatory),
            comments: string().max(
                BIRTHDAY_COMMENTS_LIMIT,
                'birthday.form.kid.comments.error'
            )
        })
    }

    openRestaurantSearchModal = () =>
        this.setState({ openRestaurantSearchModal: true })

    closeRestaurantSearchModal = () => {
        this.setState({
            openRestaurantSearchModal: false
        })
        removeLocalitiesContainerFromDOM()
    }

    setOpeningHoursToState = async (restaurantId: string) => {
        const { fetchOpeningHours } = this.props
        const openingHours = await fetchOpeningHours(restaurantId)
        const map = {
            MONDAY: 1,
            TUESDAY: 2,
            WEDNESDAY: 3,
            THURSDAY: 4,
            FRIDAY: 5,
            SATURDAY: 6,
            SUNDAY: 7
        }
        openingHours &&
            openingHours.sort((a, b) => {
                return map[a.day] - map[b.day]
            })
        openingHours && this.setState({ openingHours })
    }

    addRestaurant = async (
        setFieldValue: Function,
        restaurant: RestaurantShortDTO
    ) => {
        await this.setOpeningHoursToState(restaurant.id)
        setFieldValue('restaurantName', restaurant.name)
        setFieldValue('restaurantId', restaurant.id)
    }

    renderInactiveBirthdayForm = () => {
        return (
            <StyledMain>
                <FormattedMessage id="birthday.disabled">
                    {text => <Text>{text}</Text>}
                </FormattedMessage>
            </StyledMain>
        )
    }

    formatDateToField = (date?: string) =>
        date && date.slice(4) + '-' + date.slice(2, 4) + '-' + date.slice(0, 2)

    isDateAvailable = (date: Date) => {
        const { openingHours } = this.state
        if (openingHours.length > 0) {
            const { active = true, start, end } = openingHours[
                (date.getDay() + 6) % 7
            ]
            return active && start && end
        }
        return true
    }

    isTimeAvailable = (time: Date) => {
        const { openingHours } = this.state
        const { active = true } = openingHours[(time.getDay() + 6) % 7]
        const { formData } = this.props
        const { minHour, maxHour } = formData
        const formattedTime = time.getHours() + ':' + time.getMinutes()
        if (openingHours.length > 0) {
            const { start, end } = openingHours[(time.getDay() + 6) % 7]
            if (active && start && end) {
                return (
                    active &&
                    isTimeBetween(formattedTime, start, end, false, true)
                )
            }
        }
        return (
            active &&
            isTimeBetween(formattedTime, minHour, maxHour, false, true)
        )
    }

    renderNotConnected = () => {
        const { navigate, intl } = this.props
        const signinRoute = intl.formatMessage({
            id: 'routes.clickandcollect.signin.signup'
        })
        return (
            <ContainerPush>
                <SwitchPush
                    background="white"
                    btnColor={yellow}
                    textColor={COLOR.PRIMARY}
                    title={intl.formatMessage({
                        id: 'clickandcollect.search.connect.title'
                    })}
                    upButton={intl.formatMessage({
                        id: 'clickandcollect.search.connect.button'
                    })}
                    onClickFirstButton={() => {
                        navigate(signinRoute, {
                            fromUrl: intl.formatMessage({
                                id: 'routes.birthday'
                            })
                        })
                    }}
                    className="connect"
                />
            </ContainerPush>
        )
    }

    render() {
        const {
            formData,
            success,
            error,
            networkError,
            location,
            email,
            phone,
            kids,
            sendForm,
            sendFormError,
            resetError,
            reset,
            authenticated,
            intl
        } = this.props
        const initialKid =
            kids.length === 1
                ? kids[0]
                : { birthdate: '', firstname: '', id: '', lastname: '' }
        const { birthdate, firstname } = initialKid
        const { openRestaurantSearchModal, openingHours } = this.state
        if (error || networkError) {
            return <ErrorManagement error={error} networkError={networkError} />
        }
        if (formData) {
            const {
                perGuestPrice,
                minGuests,
                maxGuests,
                noReservationBefore,
                noReservationAfter,
                gifts,
                cakes,
                active
            } = formData
            let restaurantId = ''
            let restaurantName = ''
            /* We come from store locator or restaurant page */
            if (location.state) {
                const { id, name } = location.state
                restaurantId = id
                restaurantName = name
            }
            const legalNoticeMail = intl.formatMessage({
                id: 'faq.form.legalNotice.mail'
            })
            const noReservationAfterDate = formatDisplayDateToDate(
                noReservationAfter
            )
            const noReservationBeforeDate = formatDisplayDateToDate(
                noReservationBefore
            )
            const initialValues: any = {
                restaurantName,
                restaurantId,
                reservationDate: '',
                guests: minGuests,
                giftId: '',
                cakeId: '',
                birthdate: this.formatDateToField(birthdate),
                name: firstname,
                attendantEmail: email ? email : '',
                attendantPhone: phone ? phone : '',
                comments: '',
                promo: false,
                cgu: false
            }

            const content = (
                <Layout background="transparent">
                    <Background>
                        <ResponsiveContainer>
                            <Head>
                                <PageInfos>
                                    <FormattedMessage id="birthday.title">
                                        {(text: string) => (
                                            <H1 color="white">{text}</H1>
                                        )}
                                    </FormattedMessage>
                                    <FormattedMessage id="birthday.description">
                                        {(text: string) => (
                                            <StyledDescription color="white">
                                                {text}
                                            </StyledDescription>
                                        )}
                                    </FormattedMessage>
                                </PageInfos>
                                {!authenticated && this.renderNotConnected()}
                            </Head>
                            {!active ? (
                                this.renderInactiveBirthdayForm()
                            ) : (
                                <Main>
                                    {success ? (
                                        <Fragment>
                                            <Element>
                                                <FormattedMessage id="birthday.form.send.success.title">
                                                    {(text: string) => (
                                                        <H2
                                                            color={yellow}
                                                            center>
                                                            {text}
                                                        </H2>
                                                    )}
                                                </FormattedMessage>
                                            </Element>
                                            <Element center>
                                                <FormattedMessage id="birthday.form.send.success.message">
                                                    {(text: string) => (
                                                        <StyledText>
                                                            {text}
                                                        </StyledText>
                                                    )}
                                                </FormattedMessage>
                                            </Element>
                                            <Element>
                                                <ButtonContainer>
                                                    <StyledButtonLarge
                                                        background={yellow}
                                                        onClick={reset}>
                                                        <FormattedMessage id="birthday.form.send.success.button" />
                                                    </StyledButtonLarge>
                                                </ButtonContainer>
                                            </Element>
                                        </Fragment>
                                    ) : (
                                        <Formik
                                            initialValues={initialValues}
                                            enableReinitialize
                                            onSubmit={async (
                                                values,
                                                actions
                                            ) => {
                                                const {
                                                    restaurantId,
                                                    reservationDate,
                                                    guests,
                                                    giftId,
                                                    birthdate,
                                                    name,
                                                    attendantEmail,
                                                    attendantPhone,
                                                    comments,
                                                    promo,
                                                    cgu
                                                } = values
                                                const postedData = {
                                                    restaurantId,
                                                    reservationDate: formatDateTime(
                                                        reservationDate
                                                    ),
                                                    guests,
                                                    giftId,
                                                    cakeId: cakes[0].id,
                                                    birthdate: formatDate(
                                                        birthdate
                                                    ),
                                                    name,
                                                    attendantEmail,
                                                    attendantPhone,
                                                    comments,
                                                    optins: {
                                                        promo,
                                                        cgu
                                                    }
                                                }
                                                try {
                                                    resetError()
                                                    await sendForm(postedData)
                                                } finally {
                                                    actions.setSubmitting(false)
                                                }
                                            }}
                                            validationSchema={
                                                this.formSubscription
                                            }
                                            render={({
                                                values,
                                                errors,
                                                touched,
                                                handleBlur,
                                                handleChange,
                                                isSubmitting,
                                                setFieldValue
                                            }) => (
                                                <Form>
                                                    <FormContent>
                                                        <Block>
                                                            <Section>
                                                                <FormattedMessage id="birthday.form.restaurant.title">
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <SectionTitle>
                                                                            {
                                                                                text
                                                                            }
                                                                        </SectionTitle>
                                                                    )}
                                                                </FormattedMessage>
                                                                <InputHidden
                                                                    name="restaurantId"
                                                                    type="text"
                                                                />
                                                                <Input
                                                                    name="restaurantName"
                                                                    type="text"
                                                                    labelColor={
                                                                        COLOR.PRIMARY
                                                                    }
                                                                    TextStyle={
                                                                        InputTextStyle
                                                                    }
                                                                    handleClick={
                                                                        this
                                                                            .openRestaurantSearchModal
                                                                    }
                                                                    labelKey="birthday.form.restaurant.name"
                                                                    placeholderKey="birthday.form.restaurant.name.placeholder"
                                                                    className={`birthdayRestaurant ${errors.restaurantName &&
                                                                        touched.restaurantName &&
                                                                        'error'}`}
                                                                />
                                                                {errors.restaurantName &&
                                                                    touched.restaurantName && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.restaurantName
                                                                            }
                                                                        />
                                                                    )}
                                                                <DatePickerField
                                                                    name="reservationDate"
                                                                    labelKey="birthday.form.restaurant.date"
                                                                    TextStyle={
                                                                        InputTextStyle
                                                                    }
                                                                    error={
                                                                        errors.reservationDate &&
                                                                        touched.reservationDate &&
                                                                        'error'
                                                                    }
                                                                    minDate={
                                                                        noReservationBeforeDate
                                                                    }
                                                                    maxDate={
                                                                        noReservationAfterDate
                                                                    }
                                                                    dateFilter={
                                                                        this
                                                                            .isDateAvailable
                                                                    }
                                                                    timeFilter={
                                                                        this
                                                                            .isTimeAvailable
                                                                    }
                                                                    showTimeSelect
                                                                    disabled={
                                                                        !openingHours ||
                                                                        (!!openingHours &&
                                                                            openingHours.length <=
                                                                                0)
                                                                    }
                                                                />
                                                                {errors.reservationDate &&
                                                                    touched.reservationDate && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.reservationDate
                                                                            }
                                                                        />
                                                                    )}
                                                                <CounterWrapper>
                                                                    <Counter
                                                                        name="guests"
                                                                        labelKey="birthday.form.restaurant.guests"
                                                                        color={
                                                                            COLOR.PRIMARY
                                                                        }
                                                                        setFieldValue={
                                                                            setFieldValue
                                                                        }
                                                                        handleChange={
                                                                            handleChange
                                                                        }
                                                                        handleBlur={
                                                                            handleBlur
                                                                        }
                                                                        value={
                                                                            values.guests
                                                                        }
                                                                        minValue={
                                                                            minGuests
                                                                        }
                                                                        maxValue={
                                                                            maxGuests
                                                                        }
                                                                    />
                                                                </CounterWrapper>
                                                                {errors.guests &&
                                                                    touched.guests && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.guests
                                                                            }
                                                                        />
                                                                    )}
                                                            </Section>
                                                            <Section>
                                                                <FormattedMessage id="birthday.form.gift.title">
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <SectionTitle>
                                                                            {
                                                                                text
                                                                            }
                                                                        </SectionTitle>
                                                                    )}
                                                                </FormattedMessage>
                                                                <FormattedMessage id="birthday.form.gift.name">
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <StyledSmallText
                                                                            color={
                                                                                grey
                                                                            }>
                                                                            {
                                                                                text
                                                                            }
                                                                        </StyledSmallText>
                                                                    )}
                                                                </FormattedMessage>
                                                                <RadioContainer className="gift">
                                                                    {gifts.map(
                                                                        (
                                                                            gift: BirthdayItem
                                                                        ) => (
                                                                            <Radio
                                                                                key={
                                                                                    gift.id
                                                                                }
                                                                                onClick={() =>
                                                                                    setFieldValue(
                                                                                        'giftId',
                                                                                        gift.id
                                                                                    )
                                                                                }
                                                                                className="gift">
                                                                                <CheckOrRadio
                                                                                    name="giftId"
                                                                                    type="radio"
                                                                                    className="gift"
                                                                                    styledSpan
                                                                                    checked={
                                                                                        values.giftId ===
                                                                                        gift.id
                                                                                    }
                                                                                    label={
                                                                                        gift.label
                                                                                    }>
                                                                                    <Fragment>
                                                                                        {gift.image && (
                                                                                            <Image
                                                                                                alt={
                                                                                                    gift.label
                                                                                                }
                                                                                                sources={[
                                                                                                    {
                                                                                                        srcSet: toSrcSet(
                                                                                                            gift.image
                                                                                                        )
                                                                                                    }
                                                                                                ]}
                                                                                            />
                                                                                        )}
                                                                                        <InputContainer>
                                                                                            {values.giftId ===
                                                                                            gift.id ? (
                                                                                                <RadioChecked
                                                                                                    color={
                                                                                                        COLOR.PRIMARY
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                <EmptyCircle
                                                                                                    color={
                                                                                                        COLOR.PRIMARY
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                        </InputContainer>
                                                                                    </Fragment>
                                                                                </CheckOrRadio>
                                                                            </Radio>
                                                                        )
                                                                    )}
                                                                </RadioContainer>
                                                                {errors.giftId &&
                                                                    touched.giftId && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.giftId
                                                                            }
                                                                        />
                                                                    )}
                                                                <FormattedMessage id="birthday.form.gift.cake">
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <StyledSmallText>
                                                                            {
                                                                                text
                                                                            }
                                                                        </StyledSmallText>
                                                                    )}
                                                                </FormattedMessage>
                                                                <RadioContainer>
                                                                    {cakes.map(
                                                                        (
                                                                            cake: BirthdayItem
                                                                        ) => (
                                                                            <Radio
                                                                                key={
                                                                                    cake.id
                                                                                }>
                                                                                <CheckOrRadio
                                                                                    name="cakeId"
                                                                                    type="radio"
                                                                                    styledSpan
                                                                                    checked
                                                                                    label={
                                                                                        cake.label +
                                                                                        '*'
                                                                                    }>
                                                                                    <Fragment>
                                                                                        <RadioChecked
                                                                                            color={
                                                                                                COLOR.PRIMARY
                                                                                            }
                                                                                        />
                                                                                    </Fragment>
                                                                                </CheckOrRadio>
                                                                            </Radio>
                                                                        )
                                                                    )}
                                                                </RadioContainer>
                                                                {errors.cakeId &&
                                                                    touched.cakeId && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.cakeId
                                                                            }
                                                                        />
                                                                    )}
                                                            </Section>
                                                        </Block>
                                                        <Block>
                                                            <Section>
                                                                <FormattedMessage id="birthday.form.kid.title">
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <SectionTitle>
                                                                            {
                                                                                text
                                                                            }
                                                                        </SectionTitle>
                                                                    )}
                                                                </FormattedMessage>
                                                                <Input
                                                                    name="name"
                                                                    type="text"
                                                                    labelColor={
                                                                        COLOR.PRIMARY
                                                                    }
                                                                    TextStyle={
                                                                        InputTextStyle
                                                                    }
                                                                    labelKey="birthday.form.kid.name"
                                                                    placeholderKey="birthday.form.kid.name.placeholder"
                                                                    className={
                                                                        errors.name &&
                                                                        touched.name &&
                                                                        'error'
                                                                    }
                                                                />
                                                                {errors.name &&
                                                                    touched.name && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.name
                                                                            }
                                                                        />
                                                                    )}
                                                                <BirthDate>
                                                                    <Column>
                                                                        <Input
                                                                            name="birthdate"
                                                                            type="date"
                                                                            labelColor={
                                                                                COLOR.PRIMARY
                                                                            }
                                                                            TextStyle={
                                                                                InputTextStyle
                                                                            }
                                                                            labelKey="form.birthdate.title"
                                                                            placeholderKey="form.date.placeholder"
                                                                            className={`birthdate ${errors.birthdate &&
                                                                                touched.birthdate &&
                                                                                'error'}`}
                                                                        />
                                                                    </Column>
                                                                </BirthDate>
                                                                {errors.birthdate &&
                                                                    touched.birthdate && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.birthdate
                                                                            }
                                                                        />
                                                                    )}
                                                                <Input
                                                                    name="attendantEmail"
                                                                    type="email"
                                                                    labelColor={
                                                                        COLOR.PRIMARY
                                                                    }
                                                                    TextStyle={
                                                                        InputTextStyle
                                                                    }
                                                                    labelKey="birthday.form.kid.parent.email"
                                                                    placeholderKey="form.email.placeholder"
                                                                    className={
                                                                        errors.attendantEmail &&
                                                                        touched.attendantEmail &&
                                                                        'error'
                                                                    }
                                                                />
                                                                {errors.attendantEmail &&
                                                                    touched.attendantEmail && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.attendantEmail
                                                                            }
                                                                        />
                                                                    )}
                                                                <Input
                                                                    name="attendantPhone"
                                                                    type="tel"
                                                                    labelColor={
                                                                        COLOR.PRIMARY
                                                                    }
                                                                    TextStyle={
                                                                        InputTextStyle
                                                                    }
                                                                    labelKey="birthday.form.kid.parent.phone"
                                                                    placeholderKey="form.phone.placeholder"
                                                                    className={
                                                                        errors.attendantPhone &&
                                                                        touched.attendantPhone &&
                                                                        'error'
                                                                    }
                                                                />
                                                                {errors.attendantPhone &&
                                                                    touched.attendantPhone && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.attendantPhone
                                                                            }
                                                                        />
                                                                    )}
                                                                <Textarea
                                                                    name="comments"
                                                                    x
                                                                    labelKey="birthday.form.kid.comments"
                                                                    placeholderKey="birthday.form.kid.comments.placeholder"
                                                                    className={
                                                                        errors.comments &&
                                                                        touched.comments &&
                                                                        'error'
                                                                    }
                                                                    value={
                                                                        values.comments
                                                                    }
                                                                    handleChange={
                                                                        handleChange
                                                                    }
                                                                    handleBlur={
                                                                        handleBlur
                                                                    }
                                                                />

                                                                {errors.comments &&
                                                                    touched.comments && (
                                                                        <Error
                                                                            errorKey={
                                                                                errors.comments
                                                                            }
                                                                            values={{
                                                                                limit: BIRTHDAY_COMMENTS_LIMIT
                                                                            }}
                                                                        />
                                                                    )}
                                                                <FormattedMessage
                                                                    id="birthday.form.price"
                                                                    values={{
                                                                        price:
                                                                            values.guests >
                                                                            0
                                                                                ? perGuestPrice *
                                                                                  values.guests
                                                                                : 0
                                                                    }}>
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <CostText
                                                                            color={
                                                                                grey
                                                                            }>
                                                                            {
                                                                                text
                                                                            }
                                                                        </CostText>
                                                                    )}
                                                                </FormattedMessage>
                                                                <FormattedMessage
                                                                    id="birthday.form.price.infos"
                                                                    values={{
                                                                        unitPrice: perGuestPrice
                                                                    }}>
                                                                    {(
                                                                        text: string
                                                                    ) => (
                                                                        <StyledSmallText
                                                                            color={
                                                                                COLOR.PRIMARY
                                                                            }>
                                                                            {
                                                                                text
                                                                            }
                                                                        </StyledSmallText>
                                                                    )}
                                                                </FormattedMessage>
                                                            </Section>
                                                        </Block>
                                                    </FormContent>
                                                    <Agreements
                                                        onClick={() =>
                                                            setFieldValue(
                                                                'promo',
                                                                !values.promo
                                                            )
                                                        }>
                                                        <CheckOrRadio
                                                            name="promo"
                                                            type="checkbox"
                                                            labelKey="birthday.form.optin.promo"
                                                            styledSpan
                                                            checked={
                                                                values.agreements
                                                            }
                                                            className={`agreements ${errors.promo &&
                                                                touched.promo &&
                                                                'error'}`}>
                                                            <Checkbox
                                                                checked={
                                                                    values.promo
                                                                }
                                                                color={
                                                                    COLOR.PRIMARY
                                                                }
                                                            />
                                                        </CheckOrRadio>
                                                    </Agreements>
                                                    <Agreements
                                                        onClick={() =>
                                                            setFieldValue(
                                                                'cgu',
                                                                !values.cgu
                                                            )
                                                        }>
                                                        <CheckOrRadio
                                                            name="promo"
                                                            type="checkbox"
                                                            labelKey="birthday.form.optin.cgu"
                                                            checked={values.cgu}
                                                            styledSpan
                                                            className={`agreements ${errors.cgu &&
                                                                touched.cgu &&
                                                                'error'}`}>
                                                            <Checkbox
                                                                color={
                                                                    errors.cgu &&
                                                                    touched.cgu
                                                                        ? red
                                                                        : COLOR.PRIMARY
                                                                }
                                                                checked={
                                                                    values.cgu
                                                                }
                                                            />
                                                        </CheckOrRadio>
                                                    </Agreements>
                                                    {errors.cgu &&
                                                        touched.cgu && (
                                                            <Error
                                                                errorKey={
                                                                    errors.cgu
                                                                }
                                                            />
                                                        )}
                                                    <Element>
                                                        <FormattedMessage id="form.mandatory.information">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        COLOR.PRIMARY
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                    </Element>
                                                    <ButtonContainer>
                                                        <StyledButtonLarge
                                                            type="submit"
                                                            background={
                                                                COLOR.PRIMARY
                                                            }
                                                            disabled={
                                                                isSubmitting
                                                            }>
                                                            <FormattedMessage id="birthday.form.button.submit" />
                                                        </StyledButtonLarge>
                                                    </ButtonContainer>
                                                    {sendFormError && (
                                                        <Element>
                                                            <FormattedMessage
                                                                id={
                                                                    sendFormError
                                                                }
                                                                values={{
                                                                    limit: BIRTHDAY_COMMENTS_LIMIT
                                                                }}>
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <Text
                                                                        color={
                                                                            red
                                                                        }>
                                                                        {text}
                                                                    </Text>
                                                                )}
                                                            </FormattedMessage>
                                                        </Element>
                                                    )}
                                                    <Element>
                                                        <FormattedMessage id="birthday.form.legal.mentions.0">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        grey
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                        <FormattedMessage id="birthday.form.legal.mentions.1">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        grey
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                        <Link
                                                            url={`${intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'operation.tutorial.legals.link'
                                                                }
                                                            )}`}>
                                                            <FormattedMessage id="operation.tutorial.legals.link.text">
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <TinyText
                                                                        underline
                                                                        color={
                                                                            COLOR.PRIMARY
                                                                        }>
                                                                        {text}
                                                                    </TinyText>
                                                                )}
                                                            </FormattedMessage>
                                                        </Link>
                                                        <FormattedMessage id="birthday.form.legal.mentions.2">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        COLOR.PRIMARY
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                        <Link
                                                            url={`${intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'operation.tutorial.legals.link'
                                                                }
                                                            )}`}>
                                                            <FormattedMessage id="operation.tutorial.legals.link.text">
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <TinyText
                                                                        underline
                                                                        color={
                                                                            COLOR.PRIMARY
                                                                        }>
                                                                        {text}
                                                                    </TinyText>
                                                                )}
                                                            </FormattedMessage>
                                                        </Link>
                                                        <FormattedMessage id="birthday.form.legal.mentions.3">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        COLOR.PRIMARY
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                        <MailLink
                                                            href={`mailto:${legalNoticeMail}`}>
                                                            <TinyText
                                                                color={
                                                                    COLOR.PRIMARY
                                                                }
                                                                underline>
                                                                {
                                                                    legalNoticeMail
                                                                }
                                                            </TinyText>
                                                        </MailLink>
                                                        <FormattedMessage id="birthday.form.legal.mentions.4">
                                                            {(text: string) => (
                                                                <StyledTinyText
                                                                    color={
                                                                        COLOR.PRIMARY
                                                                    }>
                                                                    {text}
                                                                </StyledTinyText>
                                                            )}
                                                        </FormattedMessage>
                                                    </Element>
                                                    <RestaurantSearch
                                                        isOpen={
                                                            openRestaurantSearchModal
                                                        }
                                                        handleClose={
                                                            this
                                                                .closeRestaurantSearchModal
                                                        }
                                                        addRestaurant={(
                                                            r: RestaurantShortDTO
                                                        ) =>
                                                            this.addRestaurant(
                                                                setFieldValue,
                                                                r
                                                            )
                                                        }
                                                    />
                                                </Form>
                                            )}
                                        />
                                    )}
                                </Main>
                            )}
                        </ResponsiveContainer>
                    </Background>
                </Layout>
            )
            const params = getUrlParams(window.location.href)
            const isFromApp = getIsFromApp(params)
            return isFromApp ? (
                content
            ) : (
                <Layout background="transparent">{content}</Layout>
            )
        }
        return null
    }
}

const Background = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${backgroundBirthdayMobile});
    ${media.medium`
        background-image: url(${backgroundBirthdayDesktop});
    `};
`

const Main = styled.div`
    border-radius: 12px;
    box-shadow: 0 8px 16px 4px ${shadowBox};
    background: white;
    margin: 50px auto 500px;
    padding: 30px;
    position: relative;
    ${media.medium`
        padding: 40px;
        max-width: 600px;
    `};
`

const StyledMain = styled(Main)`
    ${media.medium`
        width: 100%;
    `}
`

const Head = styled.div`
    padding-top: 80px;
    text-align: center;
    ${media.medium`
        padding-top: 150px;
    `};
`

const PageInfos = styled.div`
    margin: auto;
    ${media.medium`
        max-width: 580px;
    `};
`

const FormContent = styled.div`
    ${media.medium`
        width: 100%;
    `};
`

const Block = styled.div`
    ${media.medium`
        width: 100%;
    `};
`

const CounterWrapper = styled.div`
    position: relative;
    width: 100%;
    font-family: ${flameSansRegular};
`

const Section = styled.section`
    margin-bottom: 25px;
    display: flex;
    justify-content: flex-start;
    align-items: baseline;
    flex-direction: column;
    width: 100%;
`

const SectionTitle = styled(H3)`
    color: ${COLOR.PRIMARY};
    margin: auto;
`

const Image = styled(Picture)`
    width: auto;
    max-height: 125px;
    ${media.medium`
        width: 100%;
        max-height: none;
    `};
`

const StyledSmallText = styled(SmallText)`
    font-family: ${flameSansRegular};
    color: ${COLOR.PRIMARY};
    margin-top: 20px;
`

const CostText = styled(StyledSmallText)`
    border-bottom: 1px solid ${grey};
    padding-bottom: 5px;
    margin-bottom: 5px;
    width: 100%;
    font-family: ${flameSansRegular};
`

const RadioContainer = styled(Section)`
    align-items: flex-start;
    flex-direction: row;
    width: 100%;
    &.gift {
        flex-wrap: wrap;
        ${media.medium`
            flex-wrap: nowrap;
        `};
        margin-bottom: 15%;
    }
`

const Radio = styled.div`
    margin-top: 10px;
    margin-right: 15px;
    font-size: 0;
    &.gift {
        width: 50%;
        margin-right: auto;
        ${media.medium`
            width: 100%;
        `};
    }
`

const InputContainer = styled.i`
    order: 1;
    margin-top: 10px;
`

const BirthDate = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    width: 100%;
`

const Column = styled.div``

const Element = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
    ${props => props.center && 'text-align: center;'};
`

const Agreements = styled(Section)`
    align-items: center;
    flex-direction: row;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const StyledButtonLarge = styled(ButtonLarge)`
    align-self: center;
    border: none;
`

const MailLink = styled.a``

const StyledText = styled(Text)`
    white-space: pre-wrap;
`

const ContainerPush = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
    ${media.medium`
        margin-top: 50px;
    `};
`

const StyledTinyText = styled(TinyText)`
    white-space: pre-line;
    color: ${COLOR.PRIMARY};
    font-family: ${flameSansRegular};
`

const InputTextStyle = styled.span`
    font-family: ${flameSansRegular};
    color: ${COLOR.PRIMARY};
`

const StyledDescription = styled(Text)`
    font-family: ${flameSansRegular};
`

export const BirthdayComponent = injectIntl(Birthday)
